import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConfigState {
  companyId: number;
  posId: number;
  posName: string;
  deviceId: string;
  features: Record<string, boolean>;

  setPosConfig: (companyId: number, posId: number, posName: string) => void;
  setDeviceId: (id: string) => void;
  setFeatures: (features: Record<string, boolean>) => void;
  clearConfig: () => void;
  isConfigured: () => boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateDeviceId(): string {
  const id =
    'PWA_CAISSE_' +
    Date.now() +
    '_' +
    Math.random().toString(36).substring(2, 11);
  return id;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULTS = {
  companyId: 0,
  posId: 0,
  posName: '',
  deviceId: '',
  features: {} as Record<string, boolean>,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setPosConfig: (companyId, posId, posName) => {
        set({ companyId, posId, posName });
      },

      setDeviceId: (id) => set({ deviceId: id }),

      setFeatures: (features) => set({ features }),

      clearConfig: () => set({ ...DEFAULTS }),

      isConfigured: () => {
        const { posId } = get();
        return posId > 0;
      },
    }),
    {
      name: 'sideview-caisse-config',
      partialize: (state) => ({
        companyId: state.companyId,
        posId: state.posId,
        posName: state.posName,
        deviceId: state.deviceId,
        features: state.features,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && !state.deviceId) {
          state.deviceId = generateDeviceId();
        }
      },
    },
  ),
);
